// web/src/console/sections/InsightsSection.tsx
import type { JSX } from "react";
import type { InsightSeries, MetricsWindow, TierBucket } from "@shared/types";
import { adminApi } from "../../lib/adminApi";
import { useConsole } from "../ConsoleContext";
import { useAsync } from "../useAsync";
import { LineChart, StatCard, StackedBar } from "../charts";
import { CardTitle, Term } from "../Term";
import { fmtMs, fmtCount, fmtPct } from "../format";

const summaryStyle = {
  fontSize: "var(--text-caption)",
  color: "var(--text-tertiary)",
  margin: "2px 0 var(--space-2)",
  fontVariantNumeric: "tabular-nums"
} as const;

function pct(value: number, total: number): string {
  return total > 0 ? `${((value / total) * 100).toFixed(1)}%` : "0.0%";
}

/** A rate-trend panel: a date-axis line of the per-bucket rate + a headline %. */
function RatePanel({ title, series, window: win, stroke }: {
  title: string; series: InsightSeries; window: MetricsWindow; stroke?: string;
}): JSX.Element {
  // Plot the per-bucket rate (0-100), so a delete-distorted absolute count never
  // drives the trend — the point of sourcing this from the immutable log.
  const points = series.points.map((b) => ({ ts: b.ts, value: b.total > 0 ? (b.value / b.total) * 100 : 0 }));
  return (
    <div className="cc-card">
      <CardTitle>{title}</CardTitle>
      <div style={summaryStyle}>窗口内 {pct(series.value, series.total)}（{series.value} / {series.total}）</div>
      <LineChart points={points} from={win.from} to={win.to} formatY={fmtPct} {...(stroke !== undefined ? { stroke } : {})} />
    </div>
  );
}

// Single source of truth for the answer-source tiers: stack/legend order, label,
// and a distinct categorical colour (design tokens) so every stacked segment is
// visually distinguishable and explained by the legend below the chart.
type TierKey = keyof Omit<TierBucket, "ts">;
const TIERS: { key: TierKey; label: string; color: string }[] = [
  { key: "cache", label: "缓存", color: "var(--cc-cat-1)" },        // blue
  { key: "kb_high", label: "高置信", color: "var(--cc-cat-2)" },    // green
  { key: "kb_low", label: "低置信", color: "var(--cc-cat-3)" },     // amber
  { key: "web_fallback", label: "联网", color: "var(--cc-cat-4)" }, // purple
  { key: "refusal", label: "拒答", color: "var(--cc-cat-5)" },      // red
  { key: "other", label: "其他", color: "var(--cc-cat-6)" }         // teal
];

/** Stacked tier breakdown panel for 知识库命中率. */
function TierPanel({ tiers, window: win }: { tiers: TierBucket[]; window: MetricsWindow }): JSX.Element {
  const buckets = tiers.map((b) => ({
    ts: b.ts,
    segments: TIERS.filter((t) => b[t.key] > 0).map((t) => ({ value: b[t.key], color: t.color }))
  }));

  // Window totals per tier — drive the legend (colour → label + %).
  const totals = TIERS.reduce<Record<string, number>>((acc, t) => {
    acc[t.key] = tiers.reduce((s, b) => s + b[t.key], 0);
    return acc;
  }, {});
  const grand = Object.values(totals).reduce((s, v) => s + v, 0);
  const webFallbackTotal = totals.web_fallback ?? 0;
  const legend = TIERS
    .filter((t) => (totals[t.key] ?? 0) > 0)
    .map((t) => ({ label: `${t.label} ${pct(totals[t.key] ?? 0, grand)}`, color: t.color }));

  return (
    <div className="cc-card" style={{ gridColumn: "1 / -1" }}>
      <CardTitle>知识库命中率（分层）</CardTitle>
      {buckets.length > 0
        ? <StackedBar buckets={buckets} legend={legend} from={win.from} to={win.to} formatY={fmtCount} />
        : <div className="cc-empty" style={{ height: 80 }}>暂无数据</div>}
      <div style={{ ...summaryStyle, marginTop: "var(--space-2)" }}>
        <Term>联网兜底率</Term>
        {" "}{pct(webFallbackTotal, grand)}（来自 turn_completed · 已修正）
      </div>
    </div>
  );
}

/**
 * CycleCare insight panels (#228 Phase 3) — KB-hit (tiered, from turn_completed),
 * 转人工, 点踩, refusal, p95 latency and active-vs-deleted session trends. Each is
 * a HISTORICAL trend from the immutable event log + annotations, so a soft-delete
 * never distorts it. Honours the topbar's range preset and absolute from/to window.
 *
 * 知识库命中率 is now rendered as a stacked tier breakdown
 * (缓存/高置信/低置信/联网/拒答) from turn_completed.answerTier, fixing the
 * mislabeled 联网兜底率 (Phase 2, see spec §C).
 */
export function InsightsSection(): JSX.Element {
  const { query, tick } = useConsole();
  const { data, loading, error, reload } = useAsync(
    () => adminApi.insights({ range: query.range, ...(query.from !== undefined && query.to !== undefined ? { from: query.from, to: query.to } : {}) }),
    [query.range, query.from, query.to, tick]
  );
  if (loading) return <div className="cc-skeleton" style={{ height: 320 }} />;
  if (error || !data) return <div className="cc-empty">加载失败。 <button className="cc-back" onClick={reload}>重试</button></div>;

  const win = data.window;
  const latPts = data.latencyTrend.map((b) => ({ ts: b.ts, value: b.p95 }));
  const lcPts = data.lifecycleTrend.map((b) => ({ ts: b.ts, value: b.created }));
  const delPts = data.lifecycleTrend.map((b) => ({ ts: b.ts, value: b.deleted }));
  const p95Last = data.latencyTrend.at(-1)?.p95 ?? 0;
  const createdTotal = data.lifecycleTrend.reduce((s, b) => s + b.created, 0);
  const deletedTotal = data.lifecycleTrend.reduce((s, b) => s + b.deleted, 0);

  return (
    <div className="cc-grid cc-grid-2">
      <TierPanel tiers={data.tierBreakdown} window={win} />
      <RatePanel title="转人工率" series={data.handoff} window={win} stroke="var(--color-warning)" />
      <RatePanel title="点踩率" series={data.thumbsDown} window={win} stroke="var(--color-error)" />
      <RatePanel title="拒答率" series={data.refusal} window={win} stroke="var(--color-warning)" />
      <div className="cc-card">
        <CardTitle>p95 延迟趋势</CardTitle>
        <div style={summaryStyle}>末值 {fmtMs(p95Last)}</div>
        <LineChart points={latPts} from={win.from} to={win.to} formatY={fmtMs} />
      </div>
      <div className="cc-card" style={{ gridColumn: "1 / -1" }}>
        <CardTitle>会话新建 vs 删除趋势</CardTitle>
        <div style={summaryStyle}>
          <span>新建 {createdTotal}</span>
          <span style={{ marginLeft: "var(--space-3)" }}>删除 {deletedTotal}</span>
        </div>
        <div className="cc-grid cc-grid-2">
          <div>
            <StatCard label="新建会话" value={String(createdTotal)} />
            <div style={{ marginTop: "var(--space-2)" }}><LineChart points={lcPts} from={win.from} to={win.to} formatY={fmtCount} /></div>
          </div>
          <div>
            <StatCard label="删除会话" value={String(deletedTotal)} {...(deletedTotal > 0 ? { tone: "warn" as const } : {})} />
            <div style={{ marginTop: "var(--space-2)" }}><LineChart points={delPts} from={win.from} to={win.to} stroke="var(--color-error)" formatY={fmtCount} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}
